import {
  put,
  takeLatest,
  select,
  take,
  call,
} from 'redux-saga/effects';
import get from 'lodash/get';
import utils from '@/utils/auth';

import { ANALYTICS_SEND_EVENT } from '@/action-types/analytics';
import {
  USER_ADD_DATA_SUCCESS,
  USER_UPDATE_PROFILE_SUCCESS,
} from '@/action-types/user';
import {
  APP_START,
  APP_FIND_LESSON,
  TOGGLE_PROFILE,
  APP_SHOW_FEEDBACK_REQUEST,
  TOGGLE_FEEDBACK,
  APP_SET_FEEDBACK_SCREENSHOT_BLOB,
  APP_TOGGLE_STUDENT_CARD,
  CHANGE_PAGE,
  APP_ENGLISH_LEVELS_REQUEST,
  APP_ENGLISH_LEVELS_SUCCESS,
  GET_ROLE_DATA_FLOW,
} from '@/action-types/app';
import { EXERCISE_GET_EXERCISES_SUCCESS } from '@/action-types/exercise';
import {
  TEACHER_GET_INFO_REQUEST,
  TEACHER_UPDATE_STUDENT_HOMEWORK_PERCENTAGE,
} from '@/action-types/teacher';
import {
  LOGIN_LOGOUT_REQUEST,
} from '@/action-types/login';
import { STUDENT_GET_INFO_REQUEST } from '@/action-types/student';

import apiLesson from '@/api/lesson';
import apiExercise from '@/api/exercise';

import html2canvas from 'html2canvas';

import apiStudent from '@/api/student';
import apiClientState from '@/api/client-state';
import { REDIRECT_ACTION } from '@/action-types/redirect';
import { ERROR_PASS_REQUEST } from '@/action-types/error';
import { CLIENT_STATE_UPDATE } from '@/action-types/client-state';

type Action = { type: string; };

export function* getUserDataFlow() {
  while (true) {
    const { user } = yield select();

    if (!user.uid) {
      if (utils.getAccessToken()) {
        const userData = utils.parseToken(utils.getAccessToken());

        yield put({ type: USER_ADD_DATA_SUCCESS, userData });
      } else {
        //
      }
    }

    yield take(LOGIN_LOGOUT_REQUEST);
  }
}

export function* getRoleDataFlow() {
  while (true) {
    yield take(USER_ADD_DATA_SUCCESS);

    const { user, teacher, student } = yield select();

    if (user && user.role) {
      if ((user.role === 'teacher' || user.role === 'b2bAdmin') && !window.location.pathname.includes('student')) {
        if (!teacher.data.id) {
          // get teacher info
          yield put({ type: TEACHER_GET_INFO_REQUEST, uid: user.uid });
        }
      } else if (user.role === 'student' || user.role === 'b2bAdmin') {
        if (!student.data.id) {
          // get student info
          yield put({ type: STUDENT_GET_INFO_REQUEST });
        }
      } else {
        // do nothing
      }
    } else {
      // if no role, start this saga again
      yield put({ type: GET_ROLE_DATA_FLOW });
    }

    yield take(LOGIN_LOGOUT_REQUEST);
  }
}

export function* findLesson(params: {
  data: {
    sessionId: string;
    courseId: string;
    lessonId?: string;
    hash?: string;
  };
} & Action) {
  try {
    const key = (params.data.hash || '').match('home') ? 'home' : 'class';
    let { data: { lessonId } } = params;

    yield put({
      type: EXERCISE_GET_EXERCISES_SUCCESS,
      exercise: {
        currentExercise: {},
      },
    });

    if (!lessonId) {
      const lessons = yield call(apiLesson.getLessons, parseInt(params.data.courseId, 10));
      lessonId = get(lessons, 'data.getLessons[0].id');
    }

    const exercises = yield call(apiExercise.getExercises, parseInt(lessonId || '0', 10));
    const classExercises = (get(exercises, 'data.getExercises', []) || []).filter((el: {
      activity: string;
      id: number;
    }) => el.activity === key);
    const exerciseId = get(classExercises, '[0].id');

    if (lessonId && exerciseId) {
      yield put({
        type: REDIRECT_ACTION,
        payload: {
          path: `/class/${params.data.sessionId}/${params.data.courseId}/${lessonId}/${exerciseId}/`,
        },
      });
    }
  } catch (e) {
    yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'findLesson'] });
  }
}

export function* profileDataFlow({ isShow }: { isShow: boolean; } & Action) {
  // if popup opened
  if (isShow) {
    yield put({ type: ANALYTICS_SEND_EVENT, eventName: 'open-profile' });

    while (true) {
      // after success change
      yield take(USER_UPDATE_PROFILE_SUCCESS);

      // close popup
      yield put({
        type: TOGGLE_PROFILE,
        isShow: false,
      });
    }
  }
}

function toBlob(screenCanvas: HTMLCanvasElement) {
  return new Promise((resolve) => {
    screenCanvas.toBlob((blob: Blob | null) => {
      if (blob) resolve(blob);
    }, 'image/jpeg', 1.0);
  });
}

export function* showFeedbackFlow(params: {
  from: string;
} & Action) {
  yield put({
    type: ANALYTICS_SEND_EVENT,
    eventName: 'open-feedback',
    data: {
      from: params.from,
      path: window.location.pathname.split('/')[1],
    },
  });

  let screenCanvas: HTMLCanvasElement | undefined;

  // create screenshot
  try {
    screenCanvas = yield call(html2canvas, document.body, { useCORS: true });
  } catch (error) {
    // NOTE not work in firefox / safari
  }

  if (screenCanvas) {
    let blob : undefined | Blob;

    try {
      blob = yield call(toBlob, screenCanvas);
    } catch (error) {
      // NOTE not work in firefox / safari
    }

    if (blob) {
      yield put({ type: APP_SET_FEEDBACK_SCREENSHOT_BLOB, blob });
    }
  }

  // show popup
  yield put({ type: TOGGLE_FEEDBACK, isShow: true });

  // remove screenshot?
}

export function* updateData() {
  const { user } = yield select();

  if ((user.role === 'teacher' || user.role === 'b2bAdmin') && !window.location.pathname.includes('student')) {
    yield put({ type: TEACHER_GET_INFO_REQUEST, uid: user.uid });
  } else if (user.role === 'student' || user.role === 'b2bAdmin') {
    yield put({ type: STUDENT_GET_INFO_REQUEST });
  } else {
    // do nothing
  }
}

export function* getStudentData({ isShow, sessionId, listId }: {
  isShow: boolean;
  sessionId: number;
  listId: number;
} & Action) {
  if (isShow) {
    yield put({ type: ANALYTICS_SEND_EVENT, eventName: 'open-student-card' });

    try {
      const response = yield call(apiStudent.getStudentStatisticsBySession, sessionId);

      if (listId === 0) {
        const testStudentNotes = get(
          yield call(apiClientState.getClientState, 'studentCard.testStudent.comment'),
          'data.getClientState[0]',
        );

        if (testStudentNotes) {
          yield put({
            type: CLIENT_STATE_UPDATE,
            state: {
              studentCard: {
                testStudent: {
                  comment: testStudentNotes,
                },
              },
            },
          });
        }
      }

      yield put({
        type: TEACHER_UPDATE_STUDENT_HOMEWORK_PERCENTAGE,
        sessionId,
        homeworkPercentage: response.data.getStudentStatisticsBySession.homeworkPercentage,
      });
    } catch (e) {
      yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'getStudentData'] });
    }
  }
}

const sendToGA = (pathname: string): void => {
  let sendPathname = pathname;

  if (
    pathname === '/login/'
  ) {
    if (window.location.hash === '') {
      sendPathname += '#signin';
    } else if (window.location.hash === '#signin' || window.location.hash === '#signup') {
      sendPathname += window.location.hash;
    }
  }

  try {
    // @ts-ignore
    ga('set', 'page', sendPathname);
    // @ts-ignore
    ga('send', 'pageview');
  } catch (e) {
    //
  }
};

export function GAPageView({ pathname }: any) {
  sendToGA(pathname);
}

export function GAPageViewInit() {
  sendToGA(window.location.pathname);
}

export function* englishLevelsRequest() {
  const isAuth = !!utils.getAccessToken();

  if (isAuth) {
    const levels = yield call(apiLesson.getLevels);

    yield put({
      type: APP_ENGLISH_LEVELS_SUCCESS,
      levels: levels?.data?.getLevels || [],
    });
  }
}

export default function* app() {
  yield takeLatest(APP_START, getUserDataFlow);
  yield takeLatest(APP_START, getRoleDataFlow);
  yield takeLatest(GET_ROLE_DATA_FLOW, getRoleDataFlow);
  yield takeLatest(TOGGLE_PROFILE, profileDataFlow);
  yield takeLatest(USER_UPDATE_PROFILE_SUCCESS, updateData);
  yield takeLatest(APP_FIND_LESSON, findLesson);
  yield takeLatest(APP_SHOW_FEEDBACK_REQUEST, showFeedbackFlow);
  yield takeLatest(APP_TOGGLE_STUDENT_CARD, getStudentData);
  yield takeLatest(CHANGE_PAGE, GAPageView);
  yield takeLatest(APP_START, GAPageViewInit);
  yield takeLatest(APP_ENGLISH_LEVELS_REQUEST, englishLevelsRequest);
}
